import { ParsedUDI } from "@/interfaces";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
}


export function parseUDI(udi: string): ParsedUDI {
  const parser: ParsedUDI = {
    upn: '',
    expireDate: '',
    lot: '',
    type: 'GS1',
  };

  let position = 0;
  const parsedAIs = new Set<string>();

  // Definición de AIs y sus propiedades
  const AIS: { [key: string]: { length: number | null; variable: boolean } } = {
    '01': { length: 14, variable: false }, // GTIN
    '17': { length: 6, variable: false },  // Fecha de expiración
    '10': { length: null, variable: true }, // Número de lote
    // Agrega más AIs según sea necesario
  };

  // Función para extraer campos de longitud fija
  const extractFixedLength = (length: number): string => {
    const value = udi.substr(position, length);
    position += length;
    console.log(`Extraído AI de longitud fija (${length}): ${value}`);
    return value;
  };

  // Función para extraer campos de longitud variable
  const extractVariableLength = (currentAI: string): string => {
    let endPosition = position;
    while (endPosition < udi.length) {
      let isAI = false;
      for (let len = 4; len >= 2; len--) { // Priorizar AIs más largas
        const potentialAI = udi.substr(endPosition, len);
        if (
          AIS[potentialAI] &&
          !parsedAIs.has(potentialAI) &&
          potentialAI !== currentAI // Excluir el AI actual
        ) {
          isAI = true;
          break;
        }
      }
      if (isAI) {
        break; // Detenerse si se encuentra un nuevo AI
      }
      endPosition++;
    }
    const value = udi.substring(position, endPosition);
    console.log(`Extraído AI de longitud variable (${currentAI}): ${value}`);
    position = endPosition;
    return value;
  };

  // Función para detectar el siguiente AI
  const detectNextAI = (): { ai: string; length: number } | null => {
    for (let len = 4; len >= 2; len--) {
      const potentialAI = udi.substr(position, len);
      if (AIS[potentialAI]) {
        return { ai: potentialAI, length: len };
      }
    }
    return null;
  };

  // Parsear el código
  while (position < udi.length) {
    const nextAI = detectNextAI();
    if (!nextAI) {
      console.warn(`AI desconocido en la posición ${position}: ${udi.substr(position, 4)}`);
      position += 1; // Avanzar una posición si no se encuentra un AI válido
      continue;
    }

    const { ai, length } = nextAI;
    console.log(`Detectado AI: ${ai} en la posición ${position}`);
    position += length;

    // Si el AI ya ha sido parseado, saltarlo
    if (parsedAIs.has(ai)) {
      console.warn(`AI ${ai} ya ha sido parseado. Saltando.`);
      continue;
    }

    const aiProperties = AIS[ai];
    if (!aiProperties) {
      console.warn(`AI desconocido: ${ai}. Saltando.`);
      continue;
    }

    switch (ai) {
      case '01': // GTIN (14 dígitos)
        if (aiProperties.length !== null) {
          parser.upn = extractFixedLength(aiProperties.length);
          parsedAIs.add('01');
        }
        break;

      case '17': // Fecha de expiración (6 dígitos)
        if (aiProperties.length !== null) {
          const dateStr = extractFixedLength(aiProperties.length);
          // Validar y formatear la fecha
          if (/^\d{6}$/.test(dateStr)) {
            const year = dateStr.substr(0, 2);
            const month = dateStr.substr(2, 2);
            const day = dateStr.substr(4, 2);

            // Asumir años entre 2000 y 2099
            const fullYear = `20${year}`;

            // Validar mes y día
            if (
              parseInt(month, 10) >= 1 && parseInt(month, 10) <= 12 &&
              parseInt(day, 10) >= 1 && parseInt(day, 10) <= 31
            ) {
              parser.expireDate = `${year}${month}${day}`; // Almacenar como YYMMDD
            } else {
              console.error(`Fecha de expiración inválida: ${dateStr}`);
              parser.expireDate = 'Fecha inválida';
            }
          } else {
            console.error(`Formato de fecha incorrecto: ${dateStr}`);
            parser.expireDate = 'Formato de fecha incorrecto';
          }
          parsedAIs.add('17');
        }
        break;


      case '10': // Número de lote (variable)
        if (aiProperties.variable) {
          parser.lot = extractVariableLength('10');
          parsedAIs.add('10');
        }
        break;

      default:
        console.warn(`AI no manejado: ${ai}. Saltando.`);
        break;
    }
  }

  return parser;
}

// Helper function to format the expiration date (YYMMDD to DD/MM/YYYY)
export function formatExpirationDate(expireDate: string): string {
  if (expireDate.length !== 6) {
    throw new Error('Invalid expiration date format. Expected YYMMDD.');
  }
  const year = `20${expireDate.slice(0, 2)}`;
  const month = expireDate.slice(2, 4);
  const day = expireDate.slice(4, 6);
  return `${day}/${month}/${year}`;
}
/// Format UDI date from yymmdd to dd/mm/yyyy
/// Funcional hasta el 2099
export function formatUDIDate(date: string): string {
  return `${date.slice(4, 6)}/${date.slice(2, 4)}/20${date.slice(0, 2)}`;
}

// format amount no sign: '1.234.567,89'
export const formatAmount = (
  amount: number,
  // locale: string = 'es-CL', 
  decimalPlaces: number = 0
): string => {
  return '$' + new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(amount);
};



